import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, LinearProgress } from '@material-ui/core';
import { FiUploadCloud as UploadIcon } from '@react-icons/all-files/fi/FiUploadCloud';
import SiteLogo from './components/SiteLogo';
import Site from 'src/interfaces/api/Site';
import useSite from '../../../../hooks/useSite';

interface Props {
  fullWidth?: boolean;
  site?: Site;
}

const SiteLogoDropZone: FC<Props> = ({ site, fullWidth }) => {
  const [dragActive, setDragActive] = useState(false);
  const [logoId, setLogoId] = useState(site?.logoDocumentId);
  const { addLogo, removeLogo } = useSite();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLogoId(site?.logoDocumentId);
  }, [site]);

  const onButtonClick = () => {
    inputRef.current?.click();
  };

  const handleRemoveLogo = async () => {
    setError(undefined);
    setLoading(true);

    try {
      await removeLogo();
      setLogoId(undefined);
    } catch (e) {
      setError('Erreur lors de la suppression du logo. Veuillez réessayer');
    }

    setLoading(false);
  };

  const handleAddLogo = async (file: File) => {
    setError(undefined);
    setLoading(true);

    try {
      const documentId = await addLogo(file);
      setLogoId(documentId);
    } catch (e) {
      setError(e.message || "Erreur lors de l'ajout du logo. Veuillez réessayer");
    }

    setLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleAddLogo(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleAddLogo(e.target.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  return (
    <Box width={fullWidth && '100%'} marginBottom='1rem' color='#868686'>
      <Box display='none' clone>
        <input type='file' id='input-file-upload' onChange={handleChange} ref={inputRef} />
      </Box>

      <Box fontWeight={400} clone>
        <label>Logo du client:</label>
      </Box>

      {logoId ? (
        <SiteLogo logoId={logoId} onDelete={handleRemoveLogo} />
      ) : (
        <Box
          width={fullWidth && '100%'}
          py='20px'
          borderRadius='12px'
          border='1px dashed rgba(145, 158, 171, 0.24)'
          bgcolor={`rgba(145, 158, 171, ${dragActive ? '0.28' : '0.08'})`}
          color='#919eab'
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          clone
        >
          <ButtonBase onClick={onButtonClick}>
            <Box display='flex' flexDirection='column' alignItems='center'>
              <UploadIcon size={24} />
              <Box>Ajouter un fichier</Box>
              <Box fontSize='0.8rem' style={{ opacity: 0.6 }}>
                Logo à afficher sur le portail client
              </Box>
              {error && (
                <Box fontSize='0.8rem' color='error.main'>
                  {error}
                </Box>
              )}
            </Box>
          </ButtonBase>
        </Box>
      )}

      {loading && (
        <Box mx='5px' clone>
          <LinearProgress />
        </Box>
      )}
    </Box>
  );
};

export default SiteLogoDropZone;
